/**
 * ADMIN REQUEST BUDGET
 * ---------------------------------------------------------------------------
 * Why this file exists (root cause of the "frozen Admin Dashboard"):
 *
 * Several admin tabs mount 4-6 diagnostic panels at once and every one of them
 * fires its own edge function on mount (live DNS lookups, SMTP seed tests, the
 * 12-category ops sweep, account-integrity scoring...). Two things then go
 * wrong at the same time:
 *
 *   1. NO TIMEOUT. `supabase.functions.invoke()` uses fetch() with no deadline.
 *      A function that never answers leaves a promise pending forever, so the
 *      panel's `loading` state is never cleared and the screen sits on a
 *      spinner that never resolves. That is the "frozen" the operator sees.
 *
 *   2. NO CONCURRENCY LIMIT. A browser allows ~6 simultaneous connections per
 *      origin. Six slow edge calls to the Supabase origin saturate that pool,
 *      so every *other* request to the same origin - including the ordinary
 *      PostgREST selects the dashboard needs to render a different tab - is
 *      queued behind them. Switching tabs then does nothing for tens of
 *      seconds, which reads as the whole dashboard being hung.
 *
 * The fix is applied in ONE place (invokeEdge) instead of in 45 components:
 * every admin edge call now runs through a small scheduler that caps
 * concurrency and aborts anything past a hard deadline. A slow diagnostic can
 * still be slow, but it can no longer starve the rest of the dashboard, and it
 * can no longer hang forever.
 */

/** Maximum admin edge calls in flight at once, leaving headroom for PostgREST. */
export const ADMIN_MAX_CONCURRENCY = 3;

/** Hard deadline for a single admin edge call. */
export const ADMIN_TIMEOUT_MS = 20_000;

type Task<T> = () => Promise<T>;

let active = 0;
const waiting: Array<() => void> = [];

const acquire = (): Promise<void> =>
  new Promise((resolve) => {
    if (active < ADMIN_MAX_CONCURRENCY) {
      active += 1;
      resolve();
      return;
    }
    waiting.push(() => {
      active += 1;
      resolve();
    });
  });

const release = () => {
  active = Math.max(0, active - 1);
  const next = waiting.shift();
  if (next) next();
};

export class AdminTimeoutError extends Error {
  readonly timeoutMs: number;
  constructor(label: string, timeoutMs: number) {
    super(`"${label}" did not answer within ${Math.round(timeoutMs / 1000)}s`);
    this.name = 'AdminTimeoutError';
    this.timeoutMs = timeoutMs;
  }
}

/**
 * Run `task` under the shared admin budget: queued behind at most
 * ADMIN_MAX_CONCURRENCY peers, and rejected with AdminTimeoutError once the
 * deadline passes so the caller ALWAYS settles and can clear its spinner.
 */
export async function runAdminRequest<T>(
  label: string,
  task: Task<T>,
  timeoutMs: number = ADMIN_TIMEOUT_MS
): Promise<T> {
  await acquire();
  let timer: number | undefined;
  try {
    return await Promise.race([
      task(),
      new Promise<never>((_, reject) => {
        timer = window.setTimeout(() => reject(new AdminTimeoutError(label, timeoutMs)), timeoutMs);
      }),
    ]);
  } finally {
    if (timer !== undefined) window.clearTimeout(timer);
    release();
  }
}

/** Snapshot for diagnostics. */
export const adminQueueState = () => ({ active, queued: waiting.length, limit: ADMIN_MAX_CONCURRENCY });

/**
 * Apply the budget to EVERY `supabase.functions.invoke()` in the app.
 *
 * Wrapping the client once is deliberate: the audit found that most admin
 * panels call `supabase.functions.invoke` DIRECTLY rather than through
 * invokeEdge (AdminAccountIntegrity -> account-integrity, AdminPayments -> four
 * concurrent checkout-link probes, AdminWebhookSetup -> two at once, the email
 * tab -> four live DNS / SMTP functions). Patching 30 call sites would
 * guarantee one gets missed; patching the client cannot be bypassed.
 *
 * Contract preserved: callers still receive `{ data, error }` and are never
 * thrown into. On deadline the call resolves with
 * `{ data: null, error: AdminTimeoutError }`, so every
 * `finally { setLoading(false) }` runs and no spinner can hang forever.
 */
export function installEdgeRequestBudget(client: any) {
  const fns = client?.functions;
  if (!fns || typeof fns.invoke !== 'function' || fns.__budgetInstalled) return;

  const original = fns.invoke.bind(fns);
  fns.invoke = async (name: string, options?: any) => {
    try {
      return await runAdminRequest(name, () => original(name, options));
    } catch (e) {
      return { data: null, error: e as Error };
    }
  };
  fns.__budgetInstalled = true;
}
